import { Box, Typography } from '@mui/material';
import { TYPE } from '@/theme/typography';
import { STATUS_COLORS, getStatusColor } from '@/theme/colorTokens';

/* ── simple binary interface (backwards-compatible) ── */

interface StatusDotBinaryProps {
  active: boolean;
  activeLabel?: string;
  inactiveLabel?: string;
  status?: never;
  label?: never;
  color?: never;
}

/* ── flexible multi-status interface ── */

interface StatusDotFlexProps {
  status: string;
  label?: string;
  color?: string;
  active?: never;
  activeLabel?: never;
  inactiveLabel?: never;
}

type StatusDotProps = StatusDotBinaryProps | StatusDotFlexProps;

/**
 * Minimal status indicator — small coloured dot + muted label.
 * Replaces the old tinted-pill and MUI Chip patterns throughout the app.
 */
export default function StatusDot(props: StatusDotProps) {
  let dotColor: string;
  let text: string;

  if ('active' in props && props.active !== undefined) {
    // binary mode (backwards-compatible)
    dotColor = props.active ? STATUS_COLORS.active : STATUS_COLORS.inactive;
    text = props.active
      ? (props.activeLabel || 'Active')
      : (props.inactiveLabel || 'Inactive');
  } else {
    // flexible mode
    const p = props as StatusDotFlexProps;
    dotColor = p.color || getStatusColor(p.status);
    text = p.label || p.status.replace(/_/g, ' ');
  }

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      <Box
        sx={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          bgcolor: dotColor,
          flexShrink: 0,
        }}
      />
      <Typography
        variant="body2"
        sx={{
          fontSize: TYPE.base,
          fontWeight: TYPE.medium,
          color: 'text.secondary',
          textTransform: 'capitalize',
          lineHeight: 1,
        }}
      >
        {text}
      </Typography>
    </Box>
  );
}
